import { z } from 'zod';
import type { ApiTransport } from './http.js';

/**
 * The Blue Partner session, from the browser's side (SYS-8, SYS-10).
 *
 * **There is almost nothing here, and that is the design.** The session is an
 * `HttpOnly` cookie, so the browser holds it and JavaScript cannot read it,
 * write it, or accidentally log it. `http.ts` does not change by one line —
 * `fetch` defaults to `credentials: 'same-origin'`, so the cookie rides along
 * on every request without anybody asking it to. All that is left for this
 * module is the two acts that are not a cookie: obtaining one, and ending it.
 *
 * That is the strongest evidence ADR-0027's session seam was right. The seam
 * was built so that `lib/api.ts` could need a credential without knowing what
 * produced it; a second producer arrived, and the transport did not notice.
 */

/**
 * The one function of the transport this module needs.
 *
 * Taken as a parameter rather than the whole `ApiTransport`, on the reasoning
 * `authDriver` already carries about its mock pair: an app destructures the
 * transport at module load (`const { postJson } = createTransport(…)`), so a
 * parameter that matches what an app actually holds is the one that does not
 * force it to keep the object around.
 */
type PostJson = ApiTransport['postJson'];

/**
 * Whether a Blue Partner session can survive between this page and that API.
 *
 * **A same-site check, not a same-origin one, and the distinction is the whole
 * point.** `stbpfwebd.z13.web.core.windows.net` and `func-bpf-d.azurewebsites.net`
 * are on different registrable domains, so a cookie set by one is never sent to
 * the other — not by configuration, not by CORS, not by any header. A relative
 * base URL means the API is behind this page's own hostname, which is the only
 * arrangement in which the cookie exists at all.
 *
 * **Why this guard exists rather than just trying.** Without it, every page load
 * in a two-origin deployment would call the exchange, write a session row, and
 * have the browser discard the cookie — an orphan row per load, collected an
 * hour later by a sweep, for a credential nobody could ever present. Failing to
 * ask is better than asking and silently throwing the answer away.
 *
 * **`simulated` is the second way to answer yes** (ADR-0059), and it
 * contradicts nothing above: the cookie is still impossible cross-site, and the
 * orphan-row argument still holds for it. What changes is that such a build
 * receives the handle in the response body and sends it back in a header, so
 * there *is* a credential it can present. This function's name has always asked
 * about the session rather than the cookie, and that is now load-bearing.
 *
 * Defaulted to `false` so no existing caller changes meaning, and passed
 * explicitly by the one app that has a session at all — the operator console is
 * on an Entra bearer in every environment (ADR-0045) and never asks.
 */
export function canHoldSession(apiBaseUrl: string, simulated = false): boolean {
  return apiBaseUrl.startsWith('/') || simulated;
}

/**
 * Where a simulated build keeps the session handle (**ADR-0059**).
 *
 * **This package's own `CLAUDE.md` says a change wanting to read, store, or
 * forward a session value has stopped being a cookie and is re-deciding
 * ADR-0045. That is exactly what this is**, and the rule did its job: the ADR
 * exists, and these three functions are the whole of the exception it grants.
 * One key, and no reader outside `http.ts` and the sign-in call sites.
 *
 * **`sessionStorage`, not `localStorage`.** The cookie it stands in for carries
 * no `Max-Age` and dies with the browser; `sessionStorage` dies with the tab,
 * which is stricter rather than looser. ADR-0027 chose the same storage for the
 * MSAL cache, for the same shared squad-room and patrol-car workstations.
 *
 * **What it costs, stated plainly:** this value is readable by JavaScript, which
 * an `HttpOnly` cookie is not, so an XSS in a build carrying one steals a
 * session. That is why the API's `SIMULATED_AUTH_SITES` is a constant in the
 * repository rather than a setting, and why no environment holding real agency
 * data may turn this on.
 */
const HANDLE_KEY = 'bp.session';

/**
 * The handle this tab holds, if any.
 *
 * Guarded rather than trusted: `sessionStorage` throws on access under a few
 * browser privacy configurations, and a sign-in that failed because storage was
 * disabled would be a worse failure than one that simply has no handle.
 */
export function sessionHandle(): string | undefined {
  try {
    const stored = globalThis.sessionStorage.getItem(HANDLE_KEY);
    return stored === null || stored === '' ? undefined : stored;
  } catch {
    return undefined;
  }
}

/** Keep the handle a sign-in just returned. `undefined` is a no-op, not a clear. */
export function rememberSessionHandle(handle: string | undefined): void {
  if (handle === undefined || handle === '') return;

  try {
    globalThis.sessionStorage.setItem(HANDLE_KEY, handle);
  } catch {
    // A build that cannot store it simply has no session, which the gate
    // already renders as signed out.
  }
}

/** Drop it. On sign-out, and whenever the API says the session is gone. */
export function forgetSessionHandle(): void {
  try {
    globalThis.sessionStorage.removeItem(HANDLE_KEY);
  } catch {
    // Nothing to do, and nothing worth failing a sign-out over.
  }
}

/*
  **`openBpSession` was here, and M11 deleted it along with its route.**

  It traded an Entra access token the *browser* had fetched for a session
  cookie — the one place a bearer token was ever spendable (SYS-8). The API
  fetches that token itself now, at the passcode, and spends it in-process
  (SYS-7), so no browser holds one and there is nothing left to exchange.
  `POST /auth/session/exchange` is gone with it, and so is the `bootstrap`
  route class it was the only member of.

  `canHoldSession` above stayed, and is now the guard for both credentials
  rather than for this one: it is what tells a two-origin build that neither a
  passkey nor a passcode can complete there.
*/

/**
 * End the Blue Partner session, before whatever ends the bootstrap one.
 *
 * **Ordering matters and is the caller's to keep.** The revoke has to happen
 * while the page still exists — `signOut()` hands the tab to Entra and never
 * returns — so this is awaited first and the redirect follows. Get it backwards
 * and the row survives, which for a shared workstation is the whole of what
 * signing out was for.
 *
 * Swallows its own failure for the same reason `openBpSession` does, inverted:
 * a sign-out that refuses to proceed because one of its two halves failed is a
 * sign-out that leaves somebody signed in.
 */
export async function closeBpSession(
  postJson: PostJson,
  apiBaseUrl: string,
  simulated = false,
): Promise<void> {
  if (!canHoldSession(apiBaseUrl, simulated)) return;

  try {
    // The request carries the handle itself, via `http.ts` — which is why this
    // is awaited **before** the handle is forgotten below. Forgetting first
    // would send an unauthenticated revoke and end nothing.
    await postJson('/auth/session/revoke', z.unknown());
  } catch (cause) {
    console.error('Could not end the Blue Partner session.', cause);
  } finally {
    // **In `finally`, on this function's own reasoning inverted.** It swallows a
    // failed revoke so that sign-out still proceeds; a handle left in the tab
    // because the network was down would make that proceeding a lie. The row
    // may outlive the gesture — its idle expiry bounds that — but the browser
    // stops holding a credential the moment somebody asks it to.
    forgetSessionHandle();
  }
}
